import { Component, effect } from "@angular/core";
import { MainModel } from "../../../global/model/MainModel";
import { CampaignSettingsService } from "../../services/campaign-settings.service";
import { ConfirmationService, MessageService } from "primeng/api";
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { GridDisplayColumn } from "../../../global/model/SearchModel";
import { CardModule } from "primeng/card";
import { GenericButtonComponent } from "../../../global/component/generic/generic-button/generic-button.component";
import { GenericTableComponent } from "../../../global/component/generic/generic-table/generic-table.component";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { DialogModule } from "primeng/dialog";
import { NgIf } from "@angular/common";
import { FundType } from "../../models/CommonModels";
import { LookupService } from "../../services/lookup.service";
import { DropdownModule } from "primeng/dropdown";
import { ToggleButtonModule } from "primeng/togglebutton";
import { ToastModule } from "primeng/toast";
import { SharedService } from "../../services/shared.service";
import { CampaignService } from "../../services/campaign.service";
import { ChildCampaignApiResponse } from "../../models/UserInviteModel";
import { CSFundCode } from "../../models/CampaignSettingsModels";

@Component({
    selector: 'app-campaign-settings-fundcodes',
    standalone: true,
    imports: [
        CardModule, GenericButtonComponent, GenericTableComponent,
        ConfirmDialogModule, DialogModule,
        FormsModule, ReactiveFormsModule, ToastModule,
        NgIf, DropdownModule, ToggleButtonModule
    ],
    providers: [CampaignSettingsService, ConfirmationService, LookupService,
                MessageService, SharedService, CampaignService],
    templateUrl: './campaign-settings-fundcodes.component.html',
    styleUrl: './campaign-settings-fundcodes.component.scss'
})
export class CampaignSettingsFundCodesComponent {
  campaignId: number = -1;
  isEntityCampaign: boolean = false;
  showAddEditDialog: boolean = false;
  isEditMode: boolean = false;  
  addEditFundCode: FormGroup;
  fundTypes?: FundType[] | undefined;
  childCampaigns?: ChildCampaignApiResponse[] | undefined;

  data: CSFundCode[] = [];
  readonly CAMPAIGN_COLUMN_INDEX: number = 0;
  columns: GridDisplayColumn[] = [
      {
        columnName: "campaign",
        displayColumn: "Campaign",
        displayColumnType: "text"
      },
      {
        columnName: "fundCode",
        displayColumn: "Fund Code",
        displayColumnType: "text",
        isSortable: true,
        isFilterable: true
      },
      {
        columnName: "election",
        displayColumn: "Election",
        displayColumnType: "text",
        isSortable: true
      },
      {
        columnName: "year",
        displayColumn: "Year",
        displayColumnType: "text",
        isSortable: true
      },
      {
        columnName: "isDefault",
        displayColumn: "Default",
        displayColumnType: "formattedText",
        isSortable: true,
        isFilterable: true,
        filterType: "MultiSelect",
        filterOptions: [
          {
            key: true,
            value: 'Yes'
          },
          {
            key: false,
            value: 'No'
          }
        ]
      },
      {
        columnName: "edit",
        displayColumn: "Actions",
        displayColumnType: "actions"
      }
    ];

      
  constructor(private mainModel: MainModel,
              private campaignSettingsService: CampaignSettingsService,
              private confirmationService: ConfirmationService,
              private lookupService: LookupService,
              private messageService: MessageService,
              private sharedService: SharedService,
              private campaignService: CampaignService,
              private fb: FormBuilder) {

    this.addEditFundCode = this.fb.group({
      fundCodeId: [0],      
      childCampaignId: ['', this.isEntityCampaign ? Validators.required : ''],
      fundCode: ['', Validators.required],
      fundTypeId: ['', Validators.required],
      year: ['', Validators.required],
      isDefault: [false]
    });

    effect(() => {

      const c: any = this.mainModel.selectedCampaign();
      const campaignId: number = parseInt(c?.campaignId);
      // If campaign is not loaded, then we can't load donor flags
      if (isNaN(campaignId))
        return;

      this.campaignId = campaignId;
      this.isEntityCampaign = c.isEntity;
      this.loadFundcodes();
      this.loadChildCampaigns();
    });

  }

  loadChildCampaigns(): void {

    if (this.isEntityCampaign)
    {
      this.campaignService.getUserChildCampaigns(this.campaignId).subscribe({
        next: data => {
          this.childCampaigns = data;
          const tempColumns = [...this.columns];
          const campaignColumn = tempColumns[this.CAMPAIGN_COLUMN_INDEX];
          campaignColumn.isSortable = true;
          campaignColumn.isFilterable = true;
          campaignColumn.filterType = "MultiSelect";
          campaignColumn.filterOptions = this.childCampaigns.map(cc => { 
            return {
              key: cc.name, 
              value: cc.name
            }; 
          });
          this.columns = tempColumns; 
        },
        error: err => {
          console.error('Unable to load child campaigns. Please try again later.', err);
        }
      });
    }
  }

  loadFundcodes(): void {

    this.campaignSettingsService.getFundcodes(this.campaignId).subscribe({
      next: data => {
        this.data = data;
      },
      error: err => {
        console.error('Unable to load fundcodes. Please try again later.', err);
      },
      complete: () => console.debug("Funcodes data loading completed.")
    });
  }  

  formattedText(columnName: string, fundCode: any) {
    switch(columnName)
    {
      case "isDefault":
        return fundCode?.isDefault ? "Yes" : "No";
    }
    return '';
  }

  loadFundTypes() {
    // Load if not loaded already
    if (!this.fundTypes)
      {
        this.lookupService.getFundTypes().subscribe((data: any) => {
          this.fundTypes = data;
        });
      }
  }
  
  onActionClicked(eventData: any) {
    switch(eventData.type) {
      case "edit":
        this.onAddEditFundCode(eventData.data);
        break;
      // case "delete":
      //   this.onDeleteFundCode(eventData.data);
      //   break;
    }
  }  

  onCloseAddEditDialog() {
    this.showAddEditDialog = false;
  }

  onAddEditFundCode(fundCode?: any) {
    this.loadFundTypes();
    if (!fundCode) {
      this.isEditMode = false;
      // new fund code
      fundCode = {
        fundCodeId: 0,
        childCampaignId: '',
        fundCode: '',
        fundTypeId: '',
        year: '',
        isDefault: false
      };
    } else {
      this.isEditMode = true;
    }
    this.addEditFundCode.reset();
    this.addEditFundCode.patchValue(fundCode);
    this.showAddEditDialog = true;
  }

  onSaveFundcode() {
    if (this.isEntityCampaign && this.addEditFundCode.value.childCampaignId === "") {
      this.addEditFundCode.get('childCampaignId')?.markAllAsTouched();
    }
    if(this.addEditFundCode.value.fundCode === "") {
      this.addEditFundCode.get('fundCode')?.markAsTouched();
    }
    if(this.addEditFundCode.value.fundTypeId === "") {
      this.addEditFundCode.get('fundTypeId')?.markAsTouched();
    }
    if(this.addEditFundCode.value.year === "") {
      this.addEditFundCode.get('year')?.markAsTouched();
    }

    if (this.addEditFundCode.valid) {

      const fundCode: any = {
        fundCodeId: parseInt(this.addEditFundCode.value.fundCodeId || '', 10),
        campaignId: this.campaignId,
        childCampaignId: this.addEditFundCode.value.childCampaignId || '',
        fundCode: this.addEditFundCode.value.fundCode || '',
        fundTypeId: this.addEditFundCode.value.fundTypeId || '',
        year: this.addEditFundCode.value.year || '',
        isDefault: this.addEditFundCode.value.isDefault || false,
        isDeleted: false
      };

      this.campaignSettingsService.saveFundcode(fundCode).subscribe({
        next: (res) => { 
          window.location.reload();
        },
        error: (result) => {
          var errorMessage = 'An error occurred while trying to log in.';
          if (result?.error?.message)
            errorMessage = this.sharedService.decodeHtml(result.error.message);
          this.messageService.add({ severity: 'error', summary: 'Error', detail: errorMessage });
        }
      });
    }

    console.debug("Save fundcode clicked!")
  }
}